import styled from 'styled-components';

import {
  BREAKPOINTS,
  colors,
  FONT_FAMILIES,
} from '../../common/styles/style-constants';
import { BaseCircularButton, PrimaryButton } from '../common/styled-components';

const gradient = {
  angle: 0,
  startColor: colors.snow,
  startAlpha: 0.9,
  startPoint: 33,
  endColor: colors.black, // This color is irrelevant since the alpha is 0
  endAlpha: 0,
  endPoint: 100,
};

const generateLinearGradient = ({
  angle,
  startColor,
  startAlpha,
  startPoint,
  endColor,
  endAlpha,
  endPoint,
}) => {
  const fractionToColorHex = (fraction) =>
    Math.round(fraction * 255)
      .toString(16)
      .padStart(2, '0') || 'FF';
  const hexColorWithAlpha = (color, alpha) =>
    color.length !== 7 ? color : `${color}${alpha}`;

  const startAlphaHex = fractionToColorHex(startAlpha);
  const endAlphaHex = fractionToColorHex(endAlpha);
  const startColorWithAlpha = hexColorWithAlpha(startColor, startAlphaHex);
  const endColorWithAlpha = hexColorWithAlpha(endColor, endAlphaHex);

  return `linear-gradient(${angle}deg, ${startColorWithAlpha} ${startPoint}%, ${endColorWithAlpha} ${endPoint}%)`;
};

// Buttons
export const ButtonBlock = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  width: 100%;
  max-width: 768px;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  transform: translateX(-50%);
  padding: 20px 10px;
  background: ${generateLinearGradient(gradient)};

  /* Allow clicking through the gradient on options behind it */
  pointer-events: none;

  & > * {
    margin: 0 5px;
    /* Make sure pointer events are enabled on the CTA button */
    pointer-events: auto;
  }

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    padding-top: 40px;
    padding-bottom: 40px;
  }

  /* For screen sizes TALLER than an average 15" monitor */
  @media (min-width: ${BREAKPOINTS.tabletMin}px) and (min-height: 1030px) {
    position: absolute;
    bottom: -50px;
  }
`;

const BaseOpenSansButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  background: none;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${FONT_FAMILIES.openSans};
  font-weight: 400;
  line-height: 18px;
  font-size: 12px;
  letter-spacing: -0.015em;
  padding: 8px 20px;
  transition: all 0.1s ease-in-out;

  & > .anticon {
    margin-right: 5px;
  }
`;

export const OptimizelyPrimaryButton = styled(PrimaryButton)`
  display: block;
`;

export const SecondaryButton = styled(BaseCircularButton)`
  color: ${(props) => (props.selected ? colors.sage7 : colors.darkCharcoal)};
  background: ${(props) => (props.selected ? colors.lightSage : colors.white)};
  border: 1px solid ${(props) => (props.selected ? colors.sage6 : colors.gray4)};
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1);

  &:not(:disabled) {
    &:hover,
    &:focus {
      color: ${colors.sage6};
      border-color: ${colors.sage6};
      outline: none !important;
    }

    &:active {
      color: ${colors.sage7};
      background: ${colors.lightSage};
      border-color: ${colors.sage6};
    }
  }
`;

export const TertiaryButton = styled(BaseOpenSansButton)`
  color: ${colors.sage7};
  border: 1px solid ${colors.sage7};

  &:hover,
  &:focus {
    color: ${colors.white};
    border-color: ${colors.sage6};
    background-color: ${colors.sage6};
    outline: none !important;
  }
`;

export const QuaternaryButton = styled(BaseOpenSansButton)`
  color: ${colors.gray8};
  background: ${colors.white};
  border: 1px solid ${colors.gray4};
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1);

  &:hover,
  &:focus {
    color: ${colors.sage6};
    border-color: ${colors.sage6};
    outline: none !important;
  }
`;

export const MatchButton = styled(BaseCircularButton)`
  color: ${colors.white};
  background: ${colors.coral6};

  &:not(:disabled) {
    &:hover,
    &:focus {
      background: ${colors.coral5};
      outline: none !important;
    }

    &:active {
      background: ${colors.coral5};
    }
  }
`;

// Step Architecture
export const StepWrapper = styled.div`
  margin-top: 20px;
  padding-bottom: ${(props) =>
    (props.paddingBottom ? props.paddingBottom : '86px')};

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    width: ${(props) => props.width || '100%'};
    margin: 40px auto 0;
  }
`;

export const AdditionalBlock = styled.div`
  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    max-width: ${(props) => props.width || '600px'};
    margin: 48px auto 20px;
  }
`;

// Existing UI (match & search)
export const ExistingWrapper = styled.div`
  margin-top: 30px;
  padding: 0 15px;

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    margin-top: 60px;
    padding: 0;
  }
`;

export const ExistingButton = styled.button`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  padding: 30px;
  margin-bottom: 10px;
  text-align: center;
  width: 100%;
  color: ${colors.darkCharcoal};
  border-radius: 10px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1);
  background: ${colors.white};
  border: 1px solid ${colors.gray4};
  transition: all 0.1s ease-in-out;

  &:hover,
  &:focus {
    color: ${colors.sage7};
    border-color: ${colors.sage6};
    outline: none !important;
  }

  &:active {
    color: ${colors.sage7};
    background: ${colors.lightSage};
    border-color: ${colors.sage6};
  }

  @media (min-width: ${BREAKPOINTS.tabletMin}px) {
    max-width: 335px;
    width: 100%;
    height: 100%;
    margin: 5px;
  }
`;

export const ExistingIconWrapper = styled.div`
  position: relative;
  margin-bottom: 10px;
  padding-left: 0;
  width: ${(props) => props.width || 'auto'};
  text-align: ${(props) => props.textAlign || 'center'};
`;
